<template>
  <div>
    <router-link class="back" to="/devices"></router-link>
    <div class="wrapper">
      <h1>ONVIF Cameras</h1>
      <p>{{ deviceInfo.name || deviceInfo.id || '' }} ({{ usedCameras }} / 10)</p>

      <div class="overflowCont" v-if="canAddCameras && cameras">
        <router-link class="card" v-for="cam in cameras" :key="cam.MAC" :to="{
          name: 'AddOnvifCamera',
          params: {
            camera: cam.name, item: cam.IP,
            device: device
          }
        }">
          <div class="card-content">
            <div class="card-name">
              <div v-if="cam.is_nvr" class="name">
                <marquee>
                  <p>[NVR]
                    <span v-if="cam.nvr_name">{{ cam.nvr_name }}</span>
                    <span v-if="!cam.nvr_name">{{ cam | name }}</span>
                    <span v-if="cam.channel_name"> - {{ cam.channel_name }}</span>
                  </p>
                </marquee>
              </div>
              <div v-if="!cam.is_nvr" class="name">
                <marquee><p>{{ cam | name }}</p></marquee>
              </div>
              <div class="already-added" v-if="!cam.is_nvr && knownIps.includes(cam.IP)">
                Already tracked by this device
              </div>
            </div>
            <div class="card-info">
              <div class="ip">{{ cam.IP }}</div>
              <div class="mac">{{ cam.MAC }}</div>
            </div>
          </div>
        </router-link>
      </div>
      <div class="overflowCont" v-else-if="canAddCameras">
        <loading-wheel :time="20" initial-message="Searching for cameras..." :slowTime="5" />
      </div>
      <div class="overflowCont" v-else-if="userType == 'user'">
        <div class="card">
          <div class="card-content device-full">
            You don't have permission to add cameras to this device.
            If you believe this is an error, please contact your system administrator.
          </div>
        </div>
      </div>
      <div class="overflowCont" v-else>
        <div class="card">
          <div class="card-content device-full">
            Too many cameras are already tracked by this device.
            Please upgrade your plan to add more, or use a different device.
          </div>
        </div>
      </div>
      <router-link v-if="device" :disabled="!(email && usedCameras && canAddCameras)" :to="{
        name: 'ManuallyAddCamera',
        params: { device: device }
      }" class="button">Manually add Camera</router-link>
    </div>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import awsInfo from '../../public/websocket';
import LoadingWheel from '../components/LoadingWheel.vue';
import Marquee from '../components/Marquee.vue';

export default {
  components: { LoadingWheel, Marquee },
  data() {
    return {
      knownIps: [],
      canAddCameras: true,
      email: '',
    };
  },
  mounted() {
    Auth.currentUserInfo().then((user) => {
      this.email = user.attributes.email;
    });
  },
  filters: {
    name(camera) {
      try {
        return decodeURIComponent(camera.name);
      } catch {
        return camera.name;
      }
    },
  },
  computed: {
    device() {
      return this.$route.params.id;
    },
    cameras() {
      if (this.$store.state.onvif
        && this.$store.state.devices.currentlyCachedOnvif === this.$store.state.onvif.deviceId) {
        return this.$store.state.onvif.cameras;
      } return false;
    },
    deviceList() {
      return this.$store.state.devices.deviceList;
    },
    deviceInfo() {
      return this.deviceList.find((v) => v.id === this.device) ?? [];
    },
    currentCamera() {
      return this.$store.state.currentUpdatedCamera;
    },
    userType() {
      return this.$store.state.user?.userType?.toLowerCase() ?? 'user';
    },
    isSoldelAdmin() {
      const emailPasses = this.email?.endsWith?.('@soldelgroup.com') ?? false;
      const adminPasses = this.userType.includes('admin');
      const isSoldelAdmin = emailPasses && adminPasses;
      return isSoldelAdmin;
    },
    usedCameras() {
      try {
        return this.deviceInfo?.cameras.length ?? 0;
      } catch {
        return 0;
      }
    },

  },
  watch: {
    // this is for refreshing from this page
    device() {
      this.$store.dispatch('listCameras', this.device);
    },
    deviceList: {
      handler() {
        this.knownIps = [];
        this.fetchDeviceIps();
      },
      deep: true,
      immediate: true,
    },
    currentCamera: {
      handler() {
        const cameraUrl = this.currentCamera?.url;
        if (cameraUrl) {
          const ip = cameraUrl.match('((?:[0-2]?[0-9]{1,2}\\.){3}(?:[0-2]?[0-9]{1,2}))');
          if (ip) this.knownIps.push(ip[0]);
        }
      },
      deep: true,
      immediate: true,
    },
    usedCameras: {
      handler() {
        this.checkCameraAddAllowed();
      },
      immediate: true,
    },
    isSoldelAdmin: {
      handler() {
        this.checkCameraAddAllowed();
      },
    },
  },
  created() {
    if (this.device
      && this.device !== this.$store.state.devices.currentlyCachedOnvif
      && this.$store.state.devices.currentlyCachedOnvif !== null) {
      this.$store.dispatch('clearCachedOnvifDevice');
    }
    // if navigating this is ran
    if (this.device && this.userType !== 'user') {
      // timeout fixes refresh errors
      setTimeout(() => {
        this.$store.dispatch('listCameras', this.device);
        this.$store.dispatch('cachedOnvifDevice', this.device);
      }, 0);
    }
  },
  methods: {
    fetchDeviceIps() {
      const devices = this.deviceList.find((v) => v.id === this.device);
      if (!devices) return;
      devices.cameras.forEach((cam) => {
        awsInfo.getCameraInfo(cam.id);
      });
    },
    checkCameraAddAllowed() {
      this.canAddCameras = (this.usedCameras < 10 || this.isSoldelAdmin) && this.userType !== 'user';
    },
  },

};
</script>

<style scoped>
.button {
  margin-top: 54px;
  width: 240px;
  height: 63px;
  background-color: var(--surface-dark);
  border-radius: var(--border-radius);
}

.card {
  position: relative;
  width: clamp(301px, 80vw, 500px);
  background: var(--surface);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  display: flex;
  align-items: stretch;
  padding: 0;
  margin: 0;
}

.card-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding: 0 18px;
  min-height: 64px;
  gap: 12px;
  flex-wrap: wrap;
}

.card-content.device-full {
  color: var(--error);
  padding: 16px 18px;
  text-wrap: pretty;
}

.card-name {
  display: flex;
  flex-direction: column;
  flex: 1 1 180px;
  font-size: 18px;
  font-weight: 500;
  text-align: left;
  line-height: 1.2;
  word-break: break-word;
  min-width: 120px;
  max-width: 100%;
}

.card-name .already-added {
  font-size: 12px;
  color: var(--error);
}

.card-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  min-width: 120px;
  flex: 0 1 120px;
}

.card-info .ip {
  font-size: 16px;
  font-weight: 400;
  color: var(--text);
  margin-bottom: 2px;
}

.card-info .mac {
  font-size: 14px;
  color: var(--text);
}

.overflowCont {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;
  height: 40vh;
  margin-top: 34px;
  overflow-y: scroll;
}

.overflowCont::-webkit-scrollbar {
  display: none;
}

h1 {
  margin-top: 48px;
  font-weight: 500;
  font-size: 24px;
  letter-spacing: 0.015em;
}

.wrapper {
  margin-top: 20px;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.button {
  transition: background-color 0.3s;
}

.button[disabled] {
  pointer-events: none;
  background-color: var(--surface);
}

@media (max-width: 480px) {
  .card-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 8px 10px;
  }

  .card-name {
    min-width: 0;
    flex: 1 1 auto;
  }

  .card-info {
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
  }
}
</style>
